import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/solid';
import CIQuestionPanel from './CIQuestionPanel';
import { isPatientFemale } from '../../../../utils/conditionChecks';
import ButtonSpinner from '../../../../components/informantManagement/ButtonSpinner';
import PatientContractTypeBadge from '../../../../components/PatientContractTypeBadge';

export default function CIQuestionContentArea({
  patientId,
  prevSectionLink,
  prevSectionTitle,
  nextSectionTitle,
  currentSectionNumber,
  currentSectionTitle,
  questions,
  notes,
  handleNotesChange,
  handleNotesBlur,
  handleNextSection,
  handlePrevSection,
  navigate,
  patientFormData,
  organisation,
  assessmentType,
  subContractType,
  canEdit,
  isNavigating = false
}) {

  // The contract-type badge only belongs on the Patient Information section
  // (the clinician-facing "Patient Information" heading), not on every CI
  // section. This section title is seeded from the assessment data model and
  // is already special-cased elsewhere in the CI flow.
  const showContractTypeBadge = currentSectionTitle === 'Patient Information';

  // Function to check if a panel should be displayed based on gender
  const shouldDisplayPanel = (panel) => {
    // If we're in Medical History section and this is the Women's Health panel
    if (currentSectionTitle === "Medical History" && panel.subsectionVarName === "womensHealth") {
      // Only show this panel if the patient is female
      return isPatientFemale(patientFormData);
    }
    // For all other panels or sections, always display
    return true;
  };

  return (
    <div className="flex-grow p-8">
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={handlePrevSection}
          disabled={isNavigating}
          className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 ${isNavigating ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50'}`}
          aria-label="Back to Previous Section"
        >
          {isNavigating
            ? <ButtonSpinner className="w-5 h-5 mr-2" />
            : <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />}
          <span className="font-bold">{isNavigating ? 'Loading...' : `Back to ${prevSectionTitle}`}</span>
        </button>
        <button
          onClick={handleNextSection}
          disabled={isNavigating}
          className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition duration-100 ml-auto ${isNavigating ? 'opacity-60 cursor-not-allowed bg-violet-900 text-white' : 'hover:bg-violet-800 bg-violet-900 text-white'}`}
        >
          <span className="font-bold">{isNavigating ? 'Loading...' : `Go to ${nextSectionTitle}`}</span>
          {isNavigating
            ? <ButtonSpinner className="w-5 h-5 ml-2" />
            : <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
        </button>
      </div>
      <div className="flex justify-between items-center gap-3 mb-6">
        <h2 className="text-2xl font-bold">{currentSectionNumber}. {currentSectionTitle}</h2>
        {showContractTypeBadge && (
          <PatientContractTypeBadge
            label="Contract"
            organisation={organisation}
            assessmentType={assessmentType}
            subContractType={subContractType}
          />
        )}
      </div>

      {Object.entries(questions).map(([key, panel], panelIndex) => (
        shouldDisplayPanel(panel) ? (
          <CIQuestionPanel
            key={key}
            currentSectionNumber={currentSectionNumber}
            currentSectionTitle={currentSectionTitle}
            panel={panel}
            panelIndex={panelIndex}
            notes={notes}
            handleNotesChange={handleNotesChange}
            handleNotesBlur={handleNotesBlur}
            patientFormData={patientFormData}
            canEdit={canEdit}
          />
        ) : null
      ))}

      {/* Bottom Navigation Buttons */}
      <div className="flex justify-between items-center mt-8 pt-4 border-t border-gray-200">
        <button
          onClick={handleNextSection}
          disabled={isNavigating}
          className={`flex items-center py-2 px-4 rounded-md shadow-md transition duration-100 ml-auto ${isNavigating ? 'opacity-60 cursor-not-allowed bg-violet-900 text-white' : 'hover:bg-violet-800 bg-violet-900 text-white'}`}
        >
          <span className="font-bold">{isNavigating ? 'Loading...' : `Go to ${nextSectionTitle}`}</span>
          {isNavigating
            ? <ButtonSpinner className="w-5 h-5 ml-2" />
            : <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
        </button>
      </div>
    </div>
  );
}


